/**
 * §3.2.14 / D21 / D22 — Browse Home six-state layer visibility ownership.
 *
 * Parent (`BrowsePlaceholderPage`) is the sole mount owner for landing search,
 * category rail, promo, entity chips, and discovery control. Rail/promo components
 * stay presentational — do not rebuild them to own hide/show.
 *
 * State inventory (Frozen Authority 3a):
 *   1 Default Seznam / Provozovny
 *   2 Search-active (q.trim()>0 OR entity!=='all')
 *   3 Seznam / Tenanti
 *   4 Mapa
 *   5 ☆ favorite provozovny
 *   6 Taška★ favorite offers
 *
 * Hours / „Otevřeno“ remain NON-AUTHORITY (3c) — this module never invents open UI.
 */

import {
  isBrowseLandingSearchCollapsed,
  isDefaultBrowseSeznamScreen,
  isDefaultNakupovatSeznamPromoSurface,
  isDiscoverySearchActive,
  type DiscoverySearchParamsState,
} from '../discovery/discoverySearchParams.js';

export type BrowseHomeSixStateId = 1 | 2 | 3 | 4 | 5 | 6;

export type BrowseHomeVisibilityInput = Pick<
  DiscoverySearchParamsState,
  'q' | 'entity' | 'view' | 'listKind' | 'platformCategory'
>;

/**
 * Layer flags for Home chrome. Location is always in the Visible set for states 1–6.
 */
export type BrowseHomeLayerVisibility = {
  readonly stateId: BrowseHomeSixStateId;
  /** Location pin row — always visible on Home states 1–6. */
  readonly showLocation: true;
  /** Landing search field (+ Filtry when non-neutral). Collapsed on D22 modes. */
  readonly showSearch: boolean;
  /** Entity chips Vše|Produkty|Obchody|Tenanti — search-active only (D21 / §3.2.15). */
  readonly showEntityChips: boolean;
  /** Category rail — default Seznam Screen-1 only (hidden D21/D22). */
  readonly showCategoryRail: boolean;
  /** Promo hero — Screen-1 without platformCategory (hidden D21/D22 / D17). */
  readonly showPromo: boolean;
  /** Four-mode discovery control — hidden while search-active (state 2). */
  readonly showDiscoveryControl: boolean;
  /** Alias: state 1 (empty-q Seznam Provozovny) for nearby heading / density. */
  readonly isDefaultSeznamProvozovny: boolean;
  /**
   * Compact inset density (D23) for nearby / ☆ SP rows — directoryPickerLayout SoT.
   * True for default Seznam Provozovny and ☆ (not Tenanti / search / bag).
   */
  readonly useCompactNearbyDensity: boolean;
};

/**
 * Resolve which of the six Home states owns the current URL/params.
 * Non-list views win over search-active so Mapa/☆/Taška★ stay distinct (D22 / D03 / D04).
 */
export function resolveBrowseHomeSixState(
  state: BrowseHomeVisibilityInput,
): BrowseHomeSixStateId {
  if (state.view === 'map') {
    return 4;
  }
  if (state.view === 'favorites') {
    return 5;
  }
  if (state.view === 'bag') {
    return 6;
  }
  if (isDiscoverySearchActive(state)) {
    return 2;
  }
  if (state.listKind === 'tenant') {
    return 3;
  }
  return 1;
}

/**
 * Single ownership SSOT for Home landing-stack visibility (D21 + D22 + §3.2.14).
 * Compose existing discovery predicates — do not fork hide rules into children.
 */
export function resolveBrowseHomeLayerVisibility(
  state: BrowseHomeVisibilityInput,
): BrowseHomeLayerVisibility {
  const stateId = resolveBrowseHomeSixState(state);
  const searchActive = isDiscoverySearchActive(state);
  const isDefaultSeznamProvozovny = isDefaultBrowseSeznamScreen(state);

  return {
    stateId,
    showLocation: true,
    showSearch: !isBrowseLandingSearchCollapsed(state),
    showEntityChips: searchActive,
    showCategoryRail: isDefaultSeznamProvozovny,
    showPromo: isDefaultNakupovatSeznamPromoSurface(state),
    showDiscoveryControl: !searchActive,
    isDefaultSeznamProvozovny,
    useCompactNearbyDensity:
      isDefaultSeznamProvozovny || state.view === 'favorites',
  };
}
